import type { HTMLAttributes, ReactNode } from "react";
import type { IconName } from "../icons";
import { cn } from "../lib/cn";
import { Button } from "./button";
import { Icon } from "./icon";

interface StateProps {
  title: string;
  description?: ReactNode;
  headingLevel?: 2 | 3;
  className?: string;
}

export interface EmptyStateProps extends StateProps {
  icon?: IconName;
  action?: ReactNode;
}

/** Icon + title + helpful message + call to action (`.empty`). */
export function EmptyState({ icon = "inbox", title, description, action, headingLevel = 2, className }: EmptyStateProps) {
  const Heading = headingLevel === 2 ? "h2" : "h3";
  return (
    <div className={cn("empty", className)}>
      <span className="inline-flex size-12 items-center justify-center rounded-lg bg-primary-soft text-primary-ink">
        <Icon name={icon} className="size-6" />
      </span>
      <Heading className="mt-2 text-base font-bold text-fg">{title}</Heading>
      {description ? <p className="max-w-sm text-sm text-fg-muted">{description}</p> : null}
      {action ? <div className="mt-3 flex flex-wrap justify-center gap-2">{action}</div> : null}
    </div>
  );
}

export interface ErrorStateProps extends StateProps {
  onRetry?: () => void;
  retryLabel?: string;
}

/** Friendly message + retry. Never renders raw error text or stack traces. */
export function ErrorState({
  title,
  description,
  onRetry,
  retryLabel = "Try again",
  headingLevel = 2,
  className,
}: ErrorStateProps) {
  const Heading = headingLevel === 2 ? "h2" : "h3";
  return (
    <div role="alert" className={cn("empty", className)}>
      <span className="inline-flex size-12 items-center justify-center rounded-lg bg-danger-soft text-danger">
        <Icon name="alert-triangle" className="size-6" />
      </span>
      <Heading className="mt-2 text-base font-bold text-fg">{title}</Heading>
      {description ? <p className="max-w-sm text-sm text-fg-muted">{description}</p> : null}
      {onRetry ? (
        <Button variant="secondary" className="mt-3" onClick={onRetry}>
          <Icon name="refresh-cw" />
          {retryLabel}
        </Button>
      ) : null}
    </div>
  );
}

/** Content placeholder. Size it to match the real content (e.g. `h-8 w-[200px]`). */
export function Skeleton({ className, ...rest }: HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      aria-hidden="true"
      className={cn("animate-pulse rounded-md bg-sunken motion-reduce:animate-none", className)}
      {...rest}
    />
  );
}

export function Kbd({ className, ...rest }: HTMLAttributes<HTMLElement>) {
  return <kbd className={cn("kbd", className)} {...rest} />;
}
